import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { useBuilding, RESIDENT, printDate } from '../../state/building.js';

import LobbyReception from '../../media/images/modules/lobby-reception.jpg';

const STATUS_TONE = { 'Expected': 'info', 'Checked in': 'ok', 'Checked out': 'muted', 'Cancelled': 'muted', 'Denied': 'alert' };

const STANDING = [
    { id: 1, name: 'Marisol Castillo', role: 'Housekeeper', when: 'Tuesdays and Fridays, 9 AM to 2 PM', allowed: true },
    { id: 2, name: 'Elena and Carlos Rivera', role: 'Family', when: 'Any time, key release OK', allowed: true },
    { id: 3, name: 'Paws on Brickell', role: 'Dog walker', when: 'Weekdays, 12 PM to 1 PM', allowed: false }
];

/* Visitor Management, the resident's half. A guest registered here is
   the same record the front desk checks in. */
function GuestsScreen() {
    const { visitors, add, patch } = useBuilding();
    const [flash, notify] = useFlash();
    const [standing, setStanding] = useState(STANDING);
    const mine = visitors.filter(v => v.unit === RESIDENT.unit);

    const submit = event => {
        const values = readForm(event, ['name']);
        if (!values) return notify('Add your guest\'s name first.');

        add('visitors', {
            name: values.name,
            unit: RESIDENT.unit,
            kind: 'Guest',
            date: printDate(values.date),
            note: values.note,
            status: 'Expected'
        });
        notify(`${values.name} is on the front desk list for ${printDate(values.date)}.`);
    };

    const cancel = visitor => {
        patch('visitors', visitor.id, { status: 'Cancelled' });
        notify(`${visitor.name} removed from the front desk list.`);
    };

    const toggle = guest => {
        setStanding(list => list.map(g => (g.id === guest.id ? { ...g, allowed: !g.allowed } : g)));
        notify(guest.allowed ? `${guest.name} is paused. The desk will call you first.` : `${guest.name} can come up without a call.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Visitors" title="Your guests" />
            {flash}

            <div className="banner">
                <img src={LobbyReception} alt="" />
                <p className="banner__caption">Add a guest here and the front desk will be ready to welcome them.</p>
            </div>

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Register a guest</h4>
                    <div className="form">
                        <Field label="Guest name" wide>
                            <input name="name" className="form-control" placeholder="e.g. Jordan Blake" />
                        </Field>
                        <Field label="Date">
                            <input name="date" type="date" className="form-control" />
                        </Field>
                        <Field label="Unit">
                            <input className="form-control" value={RESIDENT.unit} readOnly />
                        </Field>
                        <Field label="Note for the desk" wide>
                            <input name="note" className="form-control" placeholder="e.g. Valet parking, send straight up" />
                        </Field>
                        <button type="submit" className="btn btn-primary">Add to guest list</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Expected and recent</h4>
                    {mine.length === 0 ? <Empty>No guests registered.</Empty> : (
                        <ul className="rows">
                            {mine.map(v => (
                                <li className="row" key={v.id}>
                                    <div className="row__main">
                                        <span className="row__title">{v.name}</span>
                                        <span className="row__meta">{v.date}{v.note ? ` · ${v.note}` : ''}</span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={STATUS_TONE[v.status] || 'muted'}>{v.status}</Chip>
                                        {v.status === 'Expected' && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => cancel(v)}>Cancel</button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <h4>Standing guests</h4>
                    <ul className="rows">
                        {standing.map(g => (
                            <li className="row" key={g.id}>
                                <div className="row__main">
                                    <span className="row__title">{g.name}</span>
                                    <span className="row__meta">{g.role} · {g.when}</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={g.allowed ? 'ok' : 'muted'}>{g.allowed ? 'Allowed' : 'Paused'}</Chip>
                                    <button type="button" className="btn btn-outline-light btn-sm" aria-pressed={g.allowed} onClick={() => toggle(g)}>
                                        {g.allowed ? 'Pause' : 'Allow'}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default GuestsScreen;
